import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
} from '@mui/material';
import {
  Psychology,
  Api,
  CheckCircle,
  Error,
  Warning,
  Refresh,
  Science,
  Visibility,
  VisibilityOff,
  ExpandMore,
  Settings,
  Cloud,
  Computer,
  Speed,
  Link,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { LoggerService } from '../services/LoggerService';

interface AIMLProvider {
  id: string;
  name: string;
  type: 'cloud' | 'local' | 'hybrid';
  provider: string;
  api_key?: string;
  api_url: string;
  is_enabled: boolean;
  is_configured: boolean;
  last_updated?: string;
  status: 'active' | 'inactive' | 'error' | 'testing';
  description: string;
  rate_limit?: number;
  max_tokens?: number;
  cost_per_token?: number;
  supported_models: string[];
  capabilities: string[];
  test_endpoint?: string;
  health_endpoint?: string;
  local_port?: number;
  local_host?: string;
}

interface AIMLTest {
  provider_id: string;
  status: 'success' | 'error' | 'testing';
  message: string;
  response_time?: number;
  last_tested?: string;
  model_response?: string;
  tokens_used?: number;
}

interface AIMLModel {
  id: string;
  name: string;
  provider_id: string;
  is_available: boolean;
  context_length: number;
  max_tokens: number;
  cost_per_1k_tokens: number;
  capabilities: string[];
}

const AIMLConfigPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [providers, setProviders] = useState<AIMLProvider[]>([]);
  const [models, setModels] = useState<AIMLModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResults, setTestResults] = useState<AIMLTest[]>([]);
  const [showApiKeys, setShowApiKeys] = useState<{ [key: string]: boolean }>({});
  const [editDialog, setEditDialog] = useState<{ open: boolean; provider: AIMLProvider | null }>({
    open: false,
    provider: null,
  });

  useEffect(() => {
    loadAIMLData();
  }, []);

  const loadAIMLData = async () => {
    try {
      setLoading(true);
      LoggerService.debug('Loading AI/ML data...');
      
      const [providersResponse, modelsResponse] = await Promise.all([
        api.get('/ai-ml/providers'),
        api.get('/ai-ml/models')
      ]);
      
      LoggerService.debug('Providers response:', providersResponse.data);
      LoggerService.debug('Models response:', modelsResponse.data);
      
      setProviders(providersResponse.data);
      setModels(modelsResponse.data);
    } catch (error: any) {
      LoggerService.error('Error loading AI/ML data:', error);
      LoggerService.error('Error details:', error.response?.data);
      enqueueSnackbar('Failed to load AI/ML configuration', { variant: 'error' });
      
      // Fallback to mock data for development
      LoggerService.debug('Using fallback mock data');
      setProviders(getMockProviders());
      setModels(getMockModels());
    } finally {
      setLoading(false);
    }
  };

  const getMockProviders = (): AIMLProvider[] => [
    {
      id: 'openai',
      name: 'OpenAI',
      type: 'cloud',
      provider: 'OpenAI Inc.',
      api_url: 'https://api.openai.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'GPT-4, GPT-3.5, and other OpenAI models',
      rate_limit: 10000,
      max_tokens: 128000,
      cost_per_token: 0.00003,
      supported_models: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo', 'gpt-4o'],
      capabilities: ['text-generation', 'chat', 'embeddings', 'function-calling'],
      test_endpoint: '/chat/completions',
      health_endpoint: '/models',
    },
    {
      id: 'deepseek',
      name: 'DeepSeek',
      type: 'cloud',
      provider: 'DeepSeek AI',
      api_url: 'https://api.deepseek.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'DeepSeek Chat and Coder models',
      rate_limit: 5000,
      max_tokens: 64000,
      cost_per_token: 0.000014,
      supported_models: ['deepseek-chat', 'deepseek-coder'],
      capabilities: ['text-generation', 'chat', 'code-generation'],
      test_endpoint: '/chat/completions',
      health_endpoint: '/models',
    },
    {
      id: 'ollama',
      name: 'Ollama',
      type: 'local',
      provider: 'Ollama',
      api_url: 'http://localhost:11434',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Local LLM hosting with various open-source models',
      rate_limit: 1000,
      max_tokens: 32000,
      cost_per_token: 0,
      supported_models: ['llama2', 'codellama', 'mistral', 'phi', 'gemma'],
      capabilities: ['text-generation', 'chat', 'code-generation'],
      test_endpoint: '/api/generate',
      health_endpoint: '/api/tags',
      local_port: 11434,
      local_host: 'localhost',
    },
    {
      id: 'lmstudio',
      name: 'LM Studio',
      type: 'local',
      provider: 'LM Studio',
      api_url: 'http://localhost:1234',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Local LLM hosting with GUI interface',
      rate_limit: 1000,
      max_tokens: 32000,
      cost_per_token: 0,
      supported_models: ['llama2', 'codellama', 'mistral', 'phi', 'gemma'],
      capabilities: ['text-generation', 'chat', 'code-generation'],
      test_endpoint: '/v1/chat/completions',
      health_endpoint: '/v1/models',
      local_port: 1234,
      local_host: 'localhost',
    },
    {
      id: 'anthropic',
      name: 'Anthropic Claude',
      type: 'cloud',
      provider: 'Anthropic',
      api_url: 'https://api.anthropic.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Claude 3.5 Sonnet and other Anthropic models',
      rate_limit: 5000,
      max_tokens: 200000,
      cost_per_token: 0.000015,
      supported_models: ['claude-3-5-sonnet', 'claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'],
      capabilities: ['text-generation', 'chat', 'analysis', 'reasoning'],
      test_endpoint: '/messages',
      health_endpoint: '/models',
    },
    {
      id: 'google',
      name: 'Google Gemini',
      type: 'cloud',
      provider: 'Google',
      api_url: 'https://generativelanguage.googleapis.com/v1',
      is_enabled: false,
      is_configured: false,
      status: 'inactive',
      description: 'Gemini Pro and other Google AI models',
      rate_limit: 15000,
      max_tokens: 32000,
      cost_per_token: 0.00001,
      supported_models: ['gemini-pro', 'gemini-pro-vision', 'gemini-1.5-pro'],
      capabilities: ['text-generation', 'chat', 'vision', 'multimodal'],
      test_endpoint: '/models/gemini-pro:generateContent',
      health_endpoint: '/models',
    },
  ];

  const getMockModels = (): AIMLModel[] => [
    {
      id: 'gpt-4',
      name: 'GPT-4',
      provider_id: 'openai',
      is_available: true,
      context_length: 128000,
      max_tokens: 4096,
      cost_per_1k_tokens: 30,
      capabilities: ['text-generation', 'chat', 'function-calling'],
    },
    {
      id: 'gpt-3.5-turbo',
      name: 'GPT-3.5 Turbo',
      provider_id: 'openai',
      is_available: true,
      context_length: 16385,
      max_tokens: 4096,
      cost_per_1k_tokens: 2,
      capabilities: ['text-generation', 'chat'],
    },
    {
      id: 'deepseek-chat',
      name: 'DeepSeek Chat',
      provider_id: 'deepseek',
      is_available: true,
      context_length: 64000,
      max_tokens: 4096,
      cost_per_1k_tokens: 14,
      capabilities: ['text-generation', 'chat', 'code-generation'],
    },
    {
      id: 'llama2',
      name: 'Llama 2',
      provider_id: 'ollama',
      is_available: true,
      context_length: 4096,
      max_tokens: 2048,
      cost_per_1k_tokens: 0,
      capabilities: ['text-generation', 'chat'],
    },
    {
      id: 'claude-3-5-sonnet',
      name: 'Claude 3.5 Sonnet',
      provider_id: 'anthropic',
      is_available: true,
      context_length: 200000,
      max_tokens: 8192,
      cost_per_1k_tokens: 15,
      capabilities: ['text-generation', 'chat', 'analysis'],
    },
  ];

  const handleToggleProvider = async (providerId: string, enabled: boolean) => {
    try {
      setSaving(true);
      await api.put(`/ai-ml/providers/${providerId}/toggle`, { enabled });
      
      setProviders(prev => prev.map(provider => 
        provider.id === providerId 
          ? { ...provider, is_enabled: enabled, status: enabled ? 'active' : 'inactive' }
          : provider
      ));
      
      enqueueSnackbar(`AI/ML provider ${enabled ? 'enabled' : 'disabled'}`, { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error toggling AI/ML provider:', error);
      enqueueSnackbar('Failed to update AI/ML provider', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestProvider = async (providerId: string) => {
    try {
      setTesting(providerId);
      const response = await api.post(`/ai-ml/providers/${providerId}/test`);
      
      const testResult: AIMLTest = {
        provider_id: providerId,
        status: response.data.status,
        message: response.data.message,
        response_time: response.data.response_time,
        last_tested: new Date().toISOString(),
        model_response: response.data.model_response,
        tokens_used: response.data.tokens_used,
      };
      
      setTestResults(prev => {
        const existing = prev.findIndex(r => r.provider_id === providerId);
        if (existing >= 0) {
          const updated = [...prev];
          updated[existing] = testResult;
          return updated;
        }
        return [...prev, testResult];
      });
      
      // Update provider status
      setProviders(prev => prev.map(provider => 
        provider.id === providerId 
          ? { ...provider, status: testResult.status === 'success' ? 'active' : 'error' }
          : provider
      ));
      
      enqueueSnackbar(`Test ${testResult.status}: ${testResult.message}`, { 
        variant: testResult.status === 'success' ? 'success' : 'error' 
      });
    } catch (error: any) {
      LoggerService.error('Error testing AI/ML provider:', error);
      enqueueSnackbar('Failed to test AI/ML provider', { variant: 'error' });
    } finally {
      setTesting(null);
    }
  };

  const handleEditProvider = (provider: AIMLProvider) => {
    setEditDialog({ open: true, provider });
  };

  const handleSaveProvider = async (provider: AIMLProvider) => {
    try {
      setSaving(true);
      await api.put(`/ai-ml/providers/${provider.id}`, provider);
      
      setProviders(prev => prev.map(p => p.id === provider.id ? provider : p));
      setEditDialog({ open: false, provider: null });
      
      enqueueSnackbar('AI/ML provider updated successfully', { variant: 'success' });
    } catch (error: any) {
      LoggerService.error('Error saving AI/ML provider:', error);
      enqueueSnackbar('Failed to save AI/ML provider', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'testing':
        return <CircularProgress size={20} />;
      default:
        return <Warning color="warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'error':
        return 'error';
      case 'testing':
        return 'info';
      default:
        return 'warning';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'cloud':
        return <Cloud />;
      case 'local':
        return <Computer />;
      case 'hybrid':
        return <Link />;
      default:
        return <Api />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'cloud':
        return 'primary';
      case 'local':
        return 'secondary';
      case 'hybrid':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTestResult = (providerId: string) => {
    return testResults.find(r => r.provider_id === providerId);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        AI/ML Configuration
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure and manage AI/ML providers and models for intelligent fund analysis and insights.
      </Typography>

      <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Cloud Providers" />
        <Tab label="Local Providers" />
        <Tab label="Models" />
        <Tab label="Capabilities" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {providers.filter(p => p.is_enabled).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Providers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="success.main">
                  {providers.filter(p => p.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Healthy Providers
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="info.main">
                  {models.filter(m => m.is_available).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available Models
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="warning.main">
                  {providers.filter(p => !p.is_configured).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Unconfigured
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Providers Table */}
          <Grid item xs={12}>
            <Card>
              <CardHeader 
                title="AI/ML Providers"
                action={
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadAIMLData}
                    disabled={loading}
                  >
                    Refresh
                  </Button>
                }
              />
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Enabled</TableCell>
                        <TableCell>Models</TableCell>
                        <TableCell>Last Updated</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getTypeIcon(provider.type)}
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {provider.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {provider.provider}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={provider.type}
                              size="small"
                              color={getTypeColor(provider.type) as any}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getStatusIcon(provider.status)}
                              <Chip 
                                label={provider.status}
                                size="small"
                                color={getStatusColor(provider.status) as any}
                                variant="outlined"
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={provider.is_enabled}
                                  onChange={(e) => handleToggleProvider(provider.id, e.target.checked)}
                                  disabled={saving}
                                />
                              }
                              label=""
                            />
                          </TableCell>
                          <TableCell>
                            <Badge badgeContent={provider.supported_models.length} color="primary">
                              <Typography variant="body2">
                                {provider.supported_models.length} models
                              </Typography>
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {provider.last_updated ? new Date(provider.last_updated).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <Tooltip title="Test Connection">
                                <IconButton
                                  size="small"
                                  onClick={() => handleTestProvider(provider.id)}
                                  disabled={testing === provider.id}
                                >
                                  {testing === provider.id ? <CircularProgress size={20} /> : <Science />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Configure">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditProvider(provider)}
                                >
                                  <Settings />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Type-specific tabs */}
      {activeTab > 0 && activeTab < 3 && (
        <Grid container spacing={3}>
          {providers
            .filter(provider => {
              if (activeTab === 1) return provider.type === 'cloud';
              if (activeTab === 2) return provider.type === 'local';
              return true;
            })
            .map((provider) => (
              <Grid item xs={12} md={6} key={provider.id}>
                <Card>
                  <CardHeader
                    title={
                      <Box display="flex" alignItems="center" gap={1}>
                        {getTypeIcon(provider.type)}
                        {provider.name}
                      </Box>
                    }
                    subheader={provider.provider}
                    action={
                      <Box display="flex" gap={1}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={provider.is_enabled}
                              onChange={(e) => handleToggleProvider(provider.id, e.target.checked)}
                              disabled={saving}
                            />
                          }
                          label=""
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleTestProvider(provider.id)}
                          disabled={testing === provider.id}
                        >
                          {testing === provider.id ? <CircularProgress size={20} /> : <Science />}
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleEditProvider(provider)}
                        >
                          <Settings />
                        </IconButton>
                      </Box>
                    }
                  />
                  <CardContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {provider.description}
                    </Typography>
                    
                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                      {getStatusIcon(provider.status)}
                      <Chip 
                        label={provider.status}
                        size="small"
                        color={getStatusColor(provider.status) as any}
                        variant="outlined"
                      />
                    </Box>

                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body2">Configuration Details</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            API URL:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.api_url}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Rate Limit:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.rate_limit} requests/hour
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Max Tokens:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.max_tokens?.toLocaleString()}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Cost per Token:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${provider.cost_per_token || 0}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Supported Models:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {provider.supported_models.map((model) => (
                              <Chip key={model} label={model} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="medium">
                            Capabilities:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
                            {provider.capabilities.map((capability) => (
                              <Chip key={capability} label={capability} size="small" color="primary" variant="outlined" />
                            ))}
                          </Box>
                        </Box>

                        {getTestResult(provider.id) && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="medium">
                              Last Test Result:
                            </Typography>
                            <Alert 
                              severity={getTestResult(provider.id)?.status === 'success' ? 'success' : 'error'}
                              sx={{ mt: 1 }}
                            >
                              {getTestResult(provider.id)?.message}
                              {getTestResult(provider.id)?.response_time && (
                                <Typography variant="caption" display="block">
                                  Response time: {getTestResult(provider.id)?.response_time}ms
                                </Typography>
                              )}
                              {getTestResult(provider.id)?.tokens_used && (
                                <Typography variant="caption" display="block">
                                  Tokens used: {getTestResult(provider.id)?.tokens_used}
                                </Typography>
                              )}
                            </Alert>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </CardContent>
                </Card>
              </Grid>
            ))}
        </Grid>
      )}

      {/* Models Tab */}
      {activeTab === 3 && (
        <Grid container spacing={3}>
          {models.map((model) => (
            <Grid item xs={12} md={6} key={model.id}>
              <Card>
                <CardHeader
                  title={model.name}
                  subheader={providers.find(p => p.id === model.provider_id)?.name}
                  action={
                    <Chip 
                      label={model.is_available ? 'Available' : 'Unavailable'}
                      color={model.is_available ? 'success' : 'error'}
                      size="small"
                    />
                  }
                />
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Context Length
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {model.context_length.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Max Tokens
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {model.max_tokens.toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Cost per 1K Tokens
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        ${model.cost_per_1k_tokens}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Capabilities
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                        {model.capabilities.map((capability) => (
                          <Chip key={capability} label={capability} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Capabilities Tab */}
      {activeTab === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Available Capabilities" />
              <CardContent>
                <List>
                  {[
                    { name: 'Text Generation', description: 'Generate human-like text responses', icon: <Psychology /> },
                    { name: 'Chat', description: 'Conversational AI interactions', icon: <Api /> },
                    { name: 'Code Generation', description: 'Generate and analyze code', icon: <Computer /> },
                    { name: 'Analysis', description: 'Data analysis and insights', icon: <Science /> },
                    { name: 'Function Calling', description: 'Execute functions and tools', icon: <Settings /> },
                    { name: 'Vision', description: 'Image and visual content analysis', icon: <Visibility /> },
                    { name: 'Multimodal', description: 'Handle multiple input types', icon: <Link /> },
                    { name: 'Reasoning', description: 'Complex logical reasoning', icon: <Speed /> },
                  ].map((capability, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>{capability.icon}</ListItemIcon>
                      <ListItemText
                        primary={capability.name}
                        secondary={capability.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardHeader title="Provider Capabilities Matrix" />
              <CardContent>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Provider</TableCell>
                        <TableCell>Text Gen</TableCell>
                        <TableCell>Chat</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Vision</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {providers.map((provider) => (
                        <TableRow key={provider.id}>
                          <TableCell>{provider.name}</TableCell>
                          <TableCell>
                            {provider.capabilities.includes('text-generation') ? '✓' : '✗'}
                          </TableCell>
                          <TableCell>
                            {provider.capabilities.includes('chat') ? '✓' : '✗'}
                          </TableCell>
                          <TableCell>
                            {provider.capabilities.includes('code-generation') ? '✓' : '✗'}
                          </TableCell>
                          <TableCell>
                            {provider.capabilities.includes('vision') ? '✓' : '✗'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, provider: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configure AI/ML Provider</DialogTitle>
        <DialogContent>
          {editDialog.provider && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API Key"
                    type={showApiKeys[editDialog.provider.id] ? 'text' : 'password'}
                    value={editDialog.provider.api_key || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      provider: { ...editDialog.provider!, api_key: e.target.value }
                    })}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowApiKeys(prev => ({
                            ...prev,
                            [editDialog.provider!.id]: !prev[editDialog.provider!.id]
                          }))}
                        >
                          {showApiKeys[editDialog.provider.id] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="API URL"
                    value={editDialog.provider.api_url}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      provider: { ...editDialog.provider!, api_url: e.target.value }
                    })}
                  />
                </Grid>
                {editDialog.provider.type === 'local' && (
                  <>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Local Host"
                        value={editDialog.provider.local_host || 'localhost'}
                        onChange={(e) => setEditDialog({
                          ...editDialog,
                          provider: { ...editDialog.provider!, local_host: e.target.value }
                        })}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Local Port"
                        type="number"
                        value={editDialog.provider.local_port || ''}
                        onChange={(e) => setEditDialog({
                          ...editDialog,
                          provider: { ...editDialog.provider!, local_port: parseInt(e.target.value) }
                        })}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Rate Limit (requests/hour)"
                    type="number"
                    value={editDialog.provider.rate_limit || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      provider: { ...editDialog.provider!, rate_limit: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Tokens"
                    type="number"
                    value={editDialog.provider.max_tokens || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      provider: { ...editDialog.provider!, max_tokens: parseInt(e.target.value) }
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cost per Token"
                    type="number"
                    value={editDialog.provider.cost_per_token || ''}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      provider: { ...editDialog.provider!, cost_per_token: parseFloat(e.target.value) }
                    })}
                    inputProps={{ step: "0.000001" }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, provider: null })}>
            Cancel
          </Button>
          <Button 
            onClick={() => editDialog.provider && handleSaveProvider(editDialog.provider)}
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIMLConfigPage;
